/**
 * Hook personnalisé pour gérer les affiliations des utilisateurs aux organisations
 * Version simplifiée qui utilise useAuth et délègue au hook de données
 */

import { useCallback } from 'react';
import { UserAffiliation, CreateUserAffiliationRequest, UpdateUserAffiliationRequest } from '@/models/userAffiliation';
import { useAuth } from '@/app/providers';
import { useUserAffiliationData } from './useUserAffiliationData';

interface UseUserAffiliationReturn {
  // État
  affiliations: UserAffiliation[];
  loading: boolean;
  error: string | null;

  // Actions
  createAffiliation: (request: CreateUserAffiliationRequest) => Promise<UserAffiliation>;
  updateAffiliation: (affiliationId: string, request: UpdateUserAffiliationRequest) => Promise<UserAffiliation>;
  deleteAffiliation: (affiliationId: string) => Promise<void>;
  updateLastActivity: (organizationId: string) => Promise<void>;

  // Utilitaires
  getUserOrganizations: () => Promise<void>;
  getOrganizationUsers: (organizationId: string) => Promise<UserAffiliation[]>;
  hasAccess: (organizationId: string) => Promise<boolean>;
  hasPermission: (organizationId: string, permission: keyof UserAffiliation['permissions']) => Promise<boolean>;

  // Getters
  getAffiliationByOrganization: (organizationId: string) => UserAffiliation | undefined;
  getCurrentUserRole: (organizationId: string) => UserAffiliation['role'] | null;
  isCurrentUserAdmin: (organizationId: string) => boolean;
  isCurrentUserOwner: (organizationId: string) => boolean;
}

export const useUserAffiliation = (): UseUserAffiliationReturn => {
  const { customer, affiliations: authAffiliations } = useAuth();
  const customerId = customer?.id || null;
  const affiliationData = useUserAffiliationData();

  // On utilise les affiliations du contexte Auth si disponibles, sinon celles du hook local
  const affiliations = authAffiliations.length > 0 ? authAffiliations : affiliationData.affiliations;

  /**
   * Charge les organisations de l'utilisateur connecté
   */
  const getUserOrganizations = useCallback(async () => {
    if (!customerId) return;
    await affiliationData.getUserOrganizations(customerId);
  }, [customerId]); // Ne pas inclure affiliationData pour éviter les re-créations

  /**
   * Charge les utilisateurs d'une organisation
   */
  const getOrganizationUsers = useCallback(async (organizationId: string): Promise<UserAffiliation[]> => {
    return await affiliationData.getOrganizationUsers(organizationId);
  }, []); // Retiré affiliationData des dépendances

  /**
   * Crée une nouvelle affiliation
   */
  const createAffiliation = useCallback(async (request: CreateUserAffiliationRequest): Promise<UserAffiliation> => {
    return await affiliationData.createAffiliation(request);
  }, []); // Retiré affiliationData des dépendances

  /**
   * Met à jour une affiliation
   */
  const updateAffiliation = useCallback(async (affiliationId: string, request: UpdateUserAffiliationRequest): Promise<UserAffiliation> => {
    return await affiliationData.updateAffiliation(affiliationId, request);
  }, []); // Retiré affiliationData des dépendances

  /**
   * Supprime une affiliation
   */
  const deleteAffiliation = useCallback(async (affiliationId: string): Promise<void> => {
    await affiliationData.deleteAffiliation(affiliationId);
  }, []); // Retiré affiliationData des dépendances

  /**
   * Met à jour la dernière activité
   */
  const updateLastActivity = useCallback(async (organizationId: string): Promise<void> => {
    if (!customerId) return;
    await affiliationData.updateLastActivity(customerId, organizationId);
  }, [customerId, affiliationData]);

  /**
   * Vérifie si l'utilisateur a accès à une organisation
   */
  const hasAccess = useCallback(async (organizationId: string): Promise<boolean> => {
    if (!customerId) return false;
    return await affiliationData.hasAccess(customerId, organizationId);
  }, [customerId, affiliationData]);

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  const hasPermission = useCallback(async (
    organizationId: string,
    permission: keyof UserAffiliation['permissions']
  ): Promise<boolean> => {
    if (!customerId) return false;
    return await affiliationData.hasPermission(customerId, organizationId, permission);
  }, [customerId, affiliationData]);

  /**
   * Récupère l'affiliation pour une organisation spécifique
   */
  const getAffiliationByOrganization = useCallback((organizationId: string): UserAffiliation | undefined => {
    return affiliationData.getAffiliationByOrganization(organizationId);
  }, [affiliationData]);

  /**
   * Récupère le rôle de l'utilisateur pour une organisation
   */
  const getCurrentUserRole = useCallback((organizationId: string): UserAffiliation['role'] | null => {
    return affiliationData.getCurrentUserRole(organizationId);
  }, [affiliationData]);

  /**
   * Vérifie si l'utilisateur est administrateur
   */
  const isCurrentUserAdmin = useCallback((organizationId: string): boolean => {
    return affiliationData.isCurrentUserAdmin(organizationId);
  }, [affiliationData]);

  /**
   * Vérifie si l'utilisateur est propriétaire
   */
  const isCurrentUserOwner = useCallback((organizationId: string): boolean => {
    return affiliationData.isCurrentUserOwner(organizationId);
  }, [affiliationData]);

  return {
    // État
    affiliations,
    loading: affiliationData.loading,
    error: affiliationData.error,

    // Actions
    createAffiliation,
    updateAffiliation,
    deleteAffiliation,
    updateLastActivity,

    // Utilitaires
    getUserOrganizations,
    getOrganizationUsers,
    hasAccess,
    hasPermission,

    // Getters
    getAffiliationByOrganization,
    getCurrentUserRole,
    isCurrentUserAdmin,
    isCurrentUserOwner,
  };
};








